import React from 'react';
import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Box } from '@mui/material';
import { Download } from '@mui/icons-material';
import { CSVData } from '../utils/csvParser';
import { downloadService } from '../services/downloadService';

interface AnalysisTableProps {
  clientData: CSVData[];
  networkData: CSVData[];
  fontFamily?: string;
}

export const AnalysisTable: React.FC<AnalysisTableProps> = ({ clientData, networkData, fontFamily = 'JetBrains Mono' }) => {
  const formatNumber = (num: number) => num.toLocaleString('en-IN');

  // Find VEHICLE-LOOKUP_INFO data
  const vehicleLookupClient = clientData.find(row => row.sub_product === 'VEHICLE-LOOKUP_INFO');
  const vehicleLookupNetwork = networkData.filter(row => row.sub_product === 'VEHICLE-LOOKUP_INFO');

  if (!vehicleLookupClient || vehicleLookupNetwork.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3, fontFamily }}>
        <Typography variant="h6" gutterBottom sx={{ fontFamily }}>
          📊 Detailed Analysis
        </Typography>
        <Typography sx={{ fontFamily }}>No VEHICLE-LOOKUP_INFO data available for analysis</Typography>
      </Paper>
    );
  }

  // Get client metrics
  const clientTotal = Number(vehicleLookupClient.total_requests) || 0;
  const clientSuccess = Number(vehicleLookupClient.successful_requests) || 0;
  const clientDataWithoutMapping = Number(vehicleLookupClient.data_without_mapping_requests) || 0;
  const clientFailed = Number(vehicleLookupClient.failed_requests) || 0;

  // Calculate total success (complete + partial)
  const clientTotalSuccess = clientSuccess + clientDataWithoutMapping;

  // Get SurePass metrics
  const surepassData = vehicleLookupNetwork.find(row => 
    String(row.destination_system).toLowerCase().includes('surepass')
  ) || {
    total_rc_calls: 0,
    total_mapping_calls: 0,
    rc_success: 0,
    mapping_success: 0,
    data_without_mapping: 0,
    failed: 0
  };

  // Calculate SurePass total success (complete + partial)
  const surepassTotalSuccess = Number(surepassData.mapping_success) + Number(surepassData.data_without_mapping);

  // Get Digitap metrics
  const digitapData = vehicleLookupNetwork.find(row => 
    String(row.destination_system).toLowerCase().includes('digitap')
  ) || {
    total_calls: 0,
    success: 0,
    failed: 0
  };

  // Calculate cases resolved by Digitap when SurePass failed which is - surepass complete success
  const casesResolvedByDigitap = clientTotalSuccess - Number(surepassData.mapping_success); 

  // Calculate success rates
  const surepassRcRate = Number(surepassData.total_rc_calls) > 0 
    ? ((Number(surepassData.rc_success) / Number(surepassData.total_rc_calls)) * 100).toFixed(1) 
    : '0.0';
  const surepassTotalSuccessRate = Number(surepassData.total_rc_calls) > 0 
    ? ((surepassTotalSuccess / Number(surepassData.total_rc_calls)) * 100).toFixed(1) 
    : '0.0';
  const digitapRate = Number(digitapData.total_calls) > 0 
    ? ((Number(digitapData.success) / Number(digitapData.total_calls)) * 100).toFixed(1) 
    : '0.0';
  const overallRate = clientTotal > 0 
    ? ((clientTotalSuccess / clientTotal) * 100).toFixed(1) 
    : '0.0';

  // Analysis data
  const analysisData = [
    {
      metric: 'VEHICLE-LOOKUP_INFO Analysis',
      value: '',
      description: 'Analysis for VEHICLE-LOOKUP_INFO sub product only',
      isHeader: true
    },
    {
      metric: 'Total Client Requests',
      value: formatNumber(clientTotal),
      description: `Overall success rate: ${overallRate}%`
    },
    {
      metric: 'Client Successful Requests',
      value: formatNumber(clientTotalSuccess),
      description: `Complete success: ${formatNumber(clientSuccess)}, Data without mapping: ${formatNumber(clientDataWithoutMapping)}`
    },
    {
      metric: 'Client Complete Success',
      value: formatNumber(clientSuccess),
      description: 'Complete success (RC + Mapping)'
    },
    {
      metric: 'Client Data Without Mapping',
      value: formatNumber(clientDataWithoutMapping),
      description: 'RC successful but mapping failed'
    },
    {
      metric: 'Client Failed Requests',
      value: formatNumber(clientFailed),
      description: `Failed requests: ${((clientFailed/clientTotal)*100).toFixed(1)}%`
    },
    {
      metric: 'SurePass RC Calls',
      value: formatNumber(Number(surepassData.total_rc_calls)),
      description: `Success rate: ${surepassRcRate}% (${formatNumber(Number(surepassData.rc_success))} successful)`
    },
    {
      metric: 'SurePass Successful Requests',
      value: formatNumber(surepassTotalSuccess),
      description: `Success rate: ${surepassTotalSuccessRate}% (Complete: ${formatNumber(Number(surepassData.mapping_success))}, Data without mapping: ${formatNumber(Number(surepassData.data_without_mapping))})`
    },
    {
      metric: 'SurePass Complete Success',
      value: formatNumber(Number(surepassData.mapping_success)),
      description: 'RC + Mapping both successful'
    },
    {
      metric: 'SurePass Data Without Mapping',
      value: formatNumber(Number(surepassData.data_without_mapping)),
      description: 'RC successful but mapping failed'
    },
    {
      metric: 'Digitap Calls',
      value: formatNumber(Number(digitapData.total_calls)),
      description: `Success rate: ${digitapRate}% (${formatNumber(Number(digitapData.success))} successful)`
    },
    {
      metric: 'Cases Resolved by Digitap',
      value: formatNumber(casesResolvedByDigitap),
      description: `Cases where SurePass failed but Digitap succeeded (${formatNumber(clientTotalSuccess)} - ${formatNumber(Number(surepassData.mapping_success))})`
    }
  ];

  // Create insights
  let insights = 'Key Insights: ';
  if (Number(surepassData.total_rc_calls) > 0) {
    insights += `SurePass RC success rate: ${surepassRcRate}%. `;
  }
  if (Number(surepassData.total_mapping_calls) > 0) {
    insights += `SurePass success rate: ${surepassTotalSuccessRate}%. `;
  }
  if (clientDataWithoutMapping > 0) {
    insights += `${formatNumber(clientDataWithoutMapping)} requests had successful RC but failed mapping. `;
  }
  if (Number(digitapData.total_calls) > 0) {
    insights += `Digitap success rate: ${digitapRate}%. `;
  }
  if (casesResolvedByDigitap > 0) {
    insights += `${formatNumber(casesResolvedByDigitap)} cases were resolved by Digitap when SurePass failed. `;
  }

  const handleDownload = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const csvData = analysisData.map(item => ({
      metric: item.metric,
      value: item.value,
      description: item.description
    }));
    downloadService.downloadTableAsCSV(csvData, `detailed_analysis_${timestamp}.csv`);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3, fontFamily }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily }}>
          📊 Detailed Analysis
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownload}
          size="small"
          sx={{ fontFamily }}
        >
          Download CSV
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#424242' }}>
              <TableCell sx={{ fontFamily, fontWeight: 'bold', color: 'white' }}>Metric</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold', color: 'white' }}>Value</TableCell>
              <TableCell sx={{ fontFamily, fontWeight: 'bold', color: 'white' }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analysisData.map((item, index) => (
              <TableRow key={index} sx={item.isHeader ? { backgroundColor: '#2196f3' } : {}}>
                {item.isHeader ? (
                  <TableCell colSpan={3} sx={{ fontFamily, fontWeight: 'bold', color: 'white' }}>
                    {item.metric} - {item.description}
                  </TableCell>
                ) : (
                  <>
                    <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>{item.metric}</TableCell>
                    <TableCell align="right" sx={{ fontFamily }}>{item.value}</TableCell>
                    <TableCell sx={{ fontFamily }}>{item.description}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
            {/* Insights Row */}
            <TableRow sx={{ backgroundColor: '#e1f5fe' }}>
              <TableCell colSpan={3} sx={{ fontFamily }}>
                <strong>{insights}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}; 